import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const TYPE_ICONS = { deposit: '💰', redeem: '💎', offer: '🎁', system: '🔔', referral: '👥', vip: '👑', wheel: '🎡' };

export default function NotificationBell() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef();

  const load = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.notifications || []);
      setUnread(data.unreadCount || 0);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [user]);

  useEffect(() => {
    if (!socket) return;
    socket.on('notification_update', load);
    return () => socket.off('notification_update', load);
  }, [socket]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setUnread(0);
    setNotifications(notifications.map(n => ({ ...n, isRead: true })));
  };

  const handleClick = async (notif) => {
    if (!notif.isRead) {
      await api.put(`/notifications/${notif._id}/read`);
      setNotifications(notifications.map(n => n._id === notif._id ? { ...n, isRead: true } : n));
      setUnread(u => Math.max(0, u - 1));
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  const deleteNotif = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`);
    setNotifications(notifications.filter(n => n._id !== id));
  };

  if (!user) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => { setOpen(!open); if (!open && unread > 0) markAllRead(); }}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', position: 'relative', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 22 }}>🔔</span>
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 0, right: 0, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900 }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, width: 340, background: 'var(--bg-card)', border: '1px solid var(--border-glow)', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 9000, overflow: 'hidden', animation: 'fadeIn 0.2s ease', marginTop: 8 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, color: '#f5c400' }}>NOTIFICATIONS</span>
            {notifications.length > 0 && (
              <button onClick={markAllRead} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>Mark all read</button>
            )}
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔔</div>
                <p style={{ fontSize: 13 }}>No notifications yet</p>
              </div>
            ) : notifications.map(n => (
              <div key={n._id} onClick={() => handleClick(n)}
                style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: n.isRead ? 'transparent' : 'rgba(245,196,0,0.04)', display: 'flex', gap: 10, alignItems: 'flex-start', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,196,0,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = n.isRead ? 'transparent' : 'rgba(245,196,0,0.04)'}>
                <span style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>{TYPE_ICONS[n.type] || '🔔'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: n.isRead ? 500 : 700, fontSize: 13, color: n.isRead ? 'var(--text-secondary)' : 'var(--text-primary)', marginBottom: 3 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{n.message}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>{new Date(n.createdAt).toLocaleString()}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  {!n.isRead && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#f5c400', flexShrink: 0 }} />}
                  <button onClick={(e) => deleteNotif(e, n._id)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, padding: '2px', opacity: 0.5 }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
